import { Class } from '@suntex.core/types';

import { Logger } from './logger.class';
import { ContextualLogger, LoggerOpts } from './types';

const CONTEXT_LOGGER = Symbol('loggedContext.logger');
const LOGGED_MARKER = Symbol('logged.wrapped');

interface LoggerContextHost {
  [CONTEXT_LOGGER]?: ContextualLogger;
}

interface LoggedFunction {
  [LOGGED_MARKER]?: boolean;
}

export type LoggedContextOpts = LoggerOpts & {
  /**
   * When `true`, automatically applies `@logged()` behavior to every method
   * on the class (own, non-constructor properties on the prototype) that
   * isn't already explicitly decorated with `@logged()`.
   */
  autoLog?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function wrapMethod<A extends any[], R>(
  propertyKey: string,
  original: (...args: A) => R,
  resolveLogger: (instance: unknown) => ContextualLogger,
): ((...args: A) => R) & LoggedFunction {
  function wrapped(this: unknown, ...args: A): R {
    try {
      const logger = resolveLogger(this).for([propertyKey]);
      logger.debug(`${propertyKey}(...)`);
      logger.trace(() => ({ args }));
    } catch (error) {
      // Logging must never throw or break the decorated call: warn to the
      // console and continue invoking the original method regardless.
      // eslint-disable-next-line no-console
      console.warn(`@logged() failed to log ${propertyKey}(...)`, error);
    }
    return original.apply(this, args);
  }
  (wrapped as typeof wrapped & LoggedFunction)[LOGGED_MARKER] = true;
  return wrapped;
}

/**
 * Class decorator factory. Creates a logger scoped to the class name (via
 * `Logger.for(() => target, opts)`) and attaches it to the class so that
 * `@logged()` (used without a `parentLogger` argument) can resolve it
 * automatically on every decorated method.
 *
 * Pass `{ autoLog: true }` to also automatically wrap every own,
 * non-constructor method on the class's prototype with `@logged()`
 * behavior, without needing to decorate each method individually. Methods
 * that are already explicitly decorated with `@logged()` are left as-is.
 */
export function loggedContext(opts?: LoggedContextOpts) {
  return function <T extends Class<object>>(target: T): T {
    (target as unknown as LoggerContextHost)[CONTEXT_LOGGER] = Logger.for(
      () => target,
      opts,
    );

    if (opts?.autoLog) {
      const proto = target.prototype as Record<string, unknown>;
      for (const key of Object.getOwnPropertyNames(proto)) {
        if (key === 'constructor') continue;

        const descriptor = Object.getOwnPropertyDescriptor(proto, key);
        if (!descriptor || typeof descriptor.value !== 'function') continue;
        if ((descriptor.value as LoggedFunction)[LOGGED_MARKER]) continue;

        descriptor.value = wrapMethod(
          key,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          descriptor.value as (...args: any[]) => any,
          (instance) => resolveContextLogger(instance),
        );
        Object.defineProperty(proto, key, descriptor);
      }
    }

    return target;
  };
}

function resolveContextLogger(instance: unknown): ContextualLogger {
  let ctor = (instance as { constructor?: unknown } | null)?.constructor as
    | (LoggerContextHost & (new (...args: never[]) => unknown))
    | undefined;

  while (ctor) {
    const logger = ctor[CONTEXT_LOGGER];
    if (logger) return logger;
    ctor = Object.getPrototypeOf(ctor) as typeof ctor;
  }

  // Logging must never throw: fall back to the global logger if no class
  // in the prototype chain has a `@loggedContext()` decorator.
  return Logger.global();
}

/**
 * Method decorator factory. Creates a child logger scoped to the decorated
 * method's name (via `parentLogger.for([propertyKey])`), and on every call
 * logs a debug message plus the call arguments at trace level, before
 * invoking the original method.
 *
 * `parentLogger` may be omitted if the containing class is decorated with
 * `@loggedContext()`, in which case the class-level logger is resolved
 * automatically at call time. Logging must never throw, so if neither a
 * `parentLogger` nor a `@loggedContext()`-provided logger is available, the
 * global logger (`Logger.global()`) is used instead.
 */
export function logged({ logger }: { logger?: ContextualLogger } = {}) {
  return function (
    _target: object,
    propertyKey: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    descriptor: TypedPropertyDescriptor<(...args: any[]) => any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): TypedPropertyDescriptor<(...args: any[]) => any> {
    const original = descriptor.value;
    if (!original) return descriptor;

    descriptor.value = wrapMethod(propertyKey, original, (instance) =>
      logger ? logger : resolveContextLogger(instance),
    );

    return descriptor;
  };
}
